<template>
  <WdsStack ref="row" class="workflow-row" justify-center>
    <TransitionGroup name="tree" appear @after-enter="calcMargins" @after-leave="calcMargins">
      <WdsStack
        v-for="(node, index) in nodes"
        :key="node.key"
        ref="itemRefs"
        direction-column
        align-center
        class="animate vertex node-hierarchy"
        :class="{
          mLN: index === 0,
          mRN: index === nodes.length - 1,
        }"
      >
        <WdsStack
          align-center
          direction-column
          :style="`z-index: ${depth - index};`"
          class="parent-node"
        >
          <div v-if="node.component" class="animate">
            <component :is="node.component" v-model="node.vModel" v-on="node.events">
              {{ node.content }}
            </component>
          </div>
          <WdsButton
            v-if="node.canAddNode"
            sm
            class="top-connector addition-icon"
            @click="$emit('add', node)"
          >
            <WdsStack justify-center align-center>
              <WdsIcon :size="16" name="plus" />
              &nbsp;
              <WdsText>{{ node.content }}</WdsText>
            </WdsStack>
          </WdsButton>
        </WdsStack>
        <WdsDivider
          class="hr animate"
          :class="{ 'no-border': node.children.length < 2 }"
          :style="marginStyles(margins[node.key])"
        />
        <transition name="tree">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <EditorNodeRender
            v-if="node.children.length > 0"
            class="child-nodes"
            :nodes="node.children"
            :depth="depth - nodes.length"
            @add="$emit('add', $event)"
            @remove="$emit('remove', $event)"
            @margins="fZip((margins[node.key] = $event), calcMargins)"
          />
        </transition>
      </WdsStack>
    </TransitionGroup>
  </WdsStack>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useElementSize } from '@vueuse/core'
import { WdsStack, WdsIcon, WdsButton, WdsDivider, WdsText } from '@worksome/wds-components'
import { fZip } from '@/utils/fZip'

const emit = defineEmits(['add', 'remove', 'margins'])

defineProps({
  nodes: {
    type: Array,
    required: true,
  },
  depth: {
    type: Number,
    default: 100,
  },
})

const row = ref(null)

const { width } = useElementSize(row)

const itemRefs = ref([])
const margins = ref({})

const marginStyles = (margin) =>
  `padding-left: ${margin?.left ?? 0}px; padding-right: ${margin?.right ?? 0}px;`
const p = (num) => parseInt(num, 10)

const calcMargins = () => {
  const els = itemRefs.value

  if (els.length < 2) {
    const w = els.length === 1 ? Math.max(els[0].$el.clientWidth, 100) / 2 : 50
    emit('margins', { left: w, right: w })
    return
  }

  const childWidth = els
    .map(({ $el }) => getComputedStyle($el))
    .reduce(
      // eslint-disable-next-line @typescript-eslint/no-shadow -- Baseline eslint ignore please fix if working on this
      (acc, { width, marginLeft, marginRight }) => p(width) + p(marginLeft) + p(marginRight) + acc,
      0,
    )
  const extraWidth = (width.value - childWidth) / 2

  const el1 = els[0].$el
  const el2 = els[els.length - 1].$el

  emit('margins', {
    left: el1.clientWidth / 2 + extraWidth,
    right: el2.clientWidth / 2 + extraWidth,
  })
}

watch(itemRefs, calcMargins)
watch(width, calcMargins)
</script>

<style lang="scss" scoped>

.workflow-row {
  --border-color: #eeeff2;
  --ani-time: 0.3s;

  @apply tw-w-full;

  .vertex {
    margin: 0 2em;
    &.mRN {
      margin-right: 0;
    }

    &.mLN {
      margin-left: 0;
    }
  }

  .animate {
    transition: all calc(var(--ani-time) / 2) linear;
  }

  .box {
    height: 100px;
    width: 100px;
    background: white;
    text-align: center;
    z-index: 8;
    border: 2px solid var(--border-color);
    border-radius: 10px;
    margin-bottom: 2px;
  }

  .addition-icon {
    margin-top: 1rem;
    border-radius: 100px;
    align-items: center;
    z-index: 20;

    &::before {
      content: '';
      width: 2px;
      height: calc(1rem);
      background: var(--border-color);
      position: absolute;
      bottom: 100%;
      left: 50%;
      margin-left: -1px;
    }
  }

  .top-connector {
    position: relative;
    overflow: visible;

    &.short-connector {
      &::before {
        height: calc(2rem - 10px);
        bottom: 14px;
      }
    }
  }

  .hr {
    margin: -12px 0 calc(1rem + 10px);
    width: 100%;
    z-index: 0;
    border-color: black;
    &.no-border {
      padding-left: 50% !important;
      padding-right: 50% !important;
    }
    ::v-deep(.wds-divider) {
      border-top-width: 2px;
      border-top-color: var(--border-color);
    }
    ::v-deep(.wds-text) {
      background: none;
      z-index: 10;
    }
  }

  .tree-enter-active,
  .tree-leave-active {
    transition: all var(--ani-time) ease;
    max-width: 1000px;
  }

  .tree-enter-from,
  .tree-leave-to {
    margin: 0;
    opacity: 0;
    z-index: 5;
    max-width: 0;
    flex-basis: 0;
  }

  .tree-enter-from {
    transform: translateY(-150px);
  }

  .tree-leave-to {
    transform: translateY(150px);
  }
}
</style>
